import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import TourV2ItineraryLocationList from 'components/OfferPage/TourV2/TourV2ItineraryLocationList'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import TourV2AllInclusionsModal from './TourV2AllInclusionsModal'
import ModalContext from 'contexts/ModalContext'
import TourV2ItemInclusions from 'components/OfferPage/TourV2/TourV2ItemInclusions'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import Group from 'components/utils/Group'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import TourDiscountLabel from 'components/Tours/TourDiscountLabel'
import useTourDiscount from 'hooks/TourV2/useTourDiscount'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowSale from 'LuxKit/PricePoints/PriceRowSale'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { getTourPricePerDay, getTourV2SavingsWithLuxPlus, getTourValueAtPerDay, getTourVariationLabel } from 'lib/tours/tourUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { getTourPriceMode } from 'selectors/TourV2/toursSelectors'

const TOUR_ID_HIDE_EXTENSION_TAG = [
  'tour-6909c91b-cc92-4c0b-86d4-d663263436be',
  'tour-46dcc839-6e5f-485c-b11f-24a55f61899a',
]

const Root = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  align-items: stretch;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.default.six};
  display: grid;
  gap: ${rem(12)};
  grid-template:
    "details" 1fr
    "pricing" min-content
    "buttons" min-content
    / 1fr;
  padding: ${rem(20)};
  position: relative;
  transition: .2s ease background, .2s ease box-shadow, .2s ease border;

  &.is-selected {
    border: 1px solid ${props => props.theme.palette.neutral.default.two};
  }

  &:not(.is-selected) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.default.six},
          ${props => props.theme.shadow.bottom.small};
      }
    }
  }

  ${mediaQueryUp.desktop} {
    grid-template:
      "details details" 1fr
      "pricing buttons" min-content
      / 1fr min-content;
    grid-row-gap: ${rem(16)};
  }
`

const DetailsContainer = styled.div`
  flex-grow: 1;
  grid-area: details;
`

const PricingContainer = styled(Group)`
  grid-area: pricing;
`

const ButtonsContainer = styled.div`
  align-items: end;
  align-self: flex-end;
  display: flex;
  flex-basis: auto;
  flex-direction: row;
  grid-area: buttons;
  justify-content: end;
  width: 100%

  ${mediaQueryUp.desktop} {
    align-items: stretch;
    align-self: flex-end;
    flex-direction: column;
    justify-self: end;
    width: 100%;
  }

  &.horizontal-view {
    ${mediaQueryUp.desktop} {
      align-self: center;
    }
  }
`

const Headsubtitle = styled(BodyText)`
  margin: ${rem(12)} 0;
`

const CaptionContainer = styled.div`
  align-items: center;
  display: flex;
  margin-top: ${rem(8)};

  > * + *::before {
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }

  &.horizontal-view {
    margin-top: ${rem(8)};
  }
`

const SelectedButton = styled(TextButton)`
  cursor: unset;
`

const ListInclusion = styled.div`
  > div {
    padding: ${rem(4)} 0;
  }
`
const LabelsContainer = styled(LabelGroup)`
  margin-top: ${rem(12)};
`

interface Props {
  isSelected: boolean
  offer: App.Tours.TourV2Offer
  variation: App.Tours.TourV2OfferVariation
  position: number
  onVariationSelect: (variationId: App.Tours.TourV2OfferVariation['id'], packageOption: string | undefined) => void
}

function TourV2VariationCard(props: Props) {
  const {
    isSelected,
    offer,
    variation,
    onVariationSelect,
    position,
  } = props

  const {
    durationInDaysCount,
    countriesCount,
    placesCount,
    hasTheSameStartAndEndLocation,
    startLocation,
    endLocation,
    inclusionItems,
    itineraryDestinations,
    itineraryDestinationsDiff,
    isBase,
    hasExtension,
    availablePackageUpgrades,
    daysCount,
  } = useTourV2VariationDetails(variation)

  const showModal = useContext(ModalContext)
  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const {
    cheapestPurchasableOption,
  } = useCheapestOfTourV2Offer(offer, variation.id)
  const { hasSomeDiscount, allIsDiscounted } = useTourDiscount(offer, variation.id)
  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(cheapestPurchasableOption)
  const savingsWithLuxPlus = useMemo(
    () => {
      if (cheapestPurchasableOption) return getTourV2SavingsWithLuxPlus(cheapestPurchasableOption)
    },
    [cheapestPurchasableOption],
  )

  const tourPriceMode = useAppSelector(getTourPriceMode)
  const pricePerDayDetails = useMemo(() => {
    if (!purchasableOptionDetails || tourPriceMode === 'total') return
    return {
      valueAtPerDay: getTourValueAtPerDay(purchasableOptionDetails.valuedAt?.price, daysCount),
      pricePerDay: getTourPricePerDay(purchasableOptionDetails.pricePerPerson, purchasableOptionDetails.memberPricePerPerson, daysCount),
    }
  }, [purchasableOptionDetails, daysCount, tourPriceMode])

  const pointsEarnCalculationOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      const nights = daysCount && daysCount > 0 ? daysCount - 1 : undefined
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestPurchasableOption?.price,
            memberPrice: cheapestPurchasableOption?.memberPrice,
            roomType: cheapestPurchasableOption?.roomType,
            packageId: cheapestPurchasableOption?.fkDepartureId,
            offerId: cheapestPurchasableOption?.fkTourId,
            nights,
          }),
      ]
    },
    [cheapestPurchasableOption, offer, daysCount],
  )

  const isLuxPlusToursAvailable = useAppSelector(state => getIsLuxPlusToursEnabled(state))

  const handleTourOptionSelect = useCallback(() => {
    onVariationSelect(variation.id, variation.packageOptionName)
  }, [onVariationSelect, variation.id, variation.packageOptionName])

  const [showInclusionInline, setShowInclusionInline] = useState(false)
  const { inclusionsToBeDisplayed, inclusionsLeft } = useMemo<{
    inclusionsToBeDisplayed: Array<App.OfferInclusion>
    inclusionsLeft: Array<App.OfferInclusion>
  }>(() => {
    const items = isBase ? inclusionItems : (inclusionItems ?? []).filter(item => item.isHighlighted)
    const inclusionsLimitToDisplay = 5
    return {
      inclusionsToBeDisplayed: items.slice(0, inclusionsLimitToDisplay),
      inclusionsLeft: items.slice(inclusionsLimitToDisplay),
    }
  }, [inclusionItems, isBase])

  const tourV2StartLocation = useMemo(
    () => {
      const variations = Object.values(offer.variations)
      if (variations.length === 0) return ['']
      const variation = variations[0]

      return [variation.startLocation || '']
    },
    [offer],
  )

  const showMoreInclusion = useCallback(() => {
    if (inclusionsLeft.length <= 2) {
      setShowInclusionInline(true)
    } else if (inclusionItems) {
      showModal(<TourV2AllInclusionsModal variation={variation} />)
    }
  }, [showModal, variation, inclusionItems, inclusionsLeft])

  const hideExtensionTag = useMemo(() => {
    return TOUR_ID_HIDE_EXTENSION_TAG.includes(offer.id)
  }, [offer.id])

  const variationLabel = useMemo(() => {
    return getTourVariationLabel({
      productType: offer.productType,
      packageOptionName: variation.packageOptionName,
    })
  }, [offer.productType, variation.packageOptionName])
  return <Root className={clsx({ 'is-selected': isSelected })} data-testid="tour-option-tile">
    <DetailsContainer>
      <Group direction="horizontal" gap={4} wrap="wrap">
        {variation.isReverseItinerary && !hideExtensionTag && <StructureLabel startIcon={<LineSyncIcon />} variant="standard">Alternate route</StructureLabel>}
        {!variation.isReverseItinerary && !!variationLabel && !hideExtensionTag && <StructureLabel variant="standard">{variationLabel}</StructureLabel>}
        {!variation.isReverseItinerary && !variationLabel && <StructureLabel variant="standard">Option {position}</StructureLabel>}
        {allIsDiscounted && <TourDiscountLabel />}
        {hasSomeDiscount && <TourDiscountLabel saleOnDepartures />}
        {
          isLuxPlusToursAvailable &&
          !!purchasableOptionDetails?.memberPrice &&
          <LabelGroup>
            <LuxPlusLabelWithModal
              offer={offer}
              saveAmount={Number(savingsWithLuxPlus)}
              type="member-price-offer-tour"
            />
          </LabelGroup>
        }
      </Group>

      <Headsubtitle variant="medium">
        {hasTheSameStartAndEndLocation && <span>Starts and ends in <b>{endLocation}</b></span>}
        {!hasTheSameStartAndEndLocation && <span>Starts in <b>{startLocation}</b>. Ends in <b>{endLocation}</b></span>}
      </Headsubtitle>
      <Heading variant="heading5" lineClamp={3}>{variation.name}</Heading>
      <VerticalSpacer gap={12}>
        <CaptionContainer>
          <BodyText variant="medium">{durationInDaysCount}</BodyText>
          {placesCount && <BodyText variant="medium">{placesCount}</BodyText>}
          {countriesCount && <BodyText variant="medium">{countriesCount}</BodyText>}
        </CaptionContainer>
        <VerticalSpacer gap={4}>
          <ListInclusion>
            {!isBase && <BodyTextBlock variant="medium" startIcon={<LineStarIcon />} weight="bold" colour="highlight-secondary">All destinations and inclusions from Base option</BodyTextBlock>}
            {variation.isReverseItinerary && !hasTheSameStartAndEndLocation && <BodyTextBlock variant="medium" startIcon={<LineSyncIcon />}>
              Starts in <b>{startLocation}</b>. Ends in <b>{endLocation}</b>
            </BodyTextBlock>}
            {!!itineraryDestinations && <TourV2ItineraryLocationList
              showAllLocations={variation.isReverseItinerary ?? false}
              prefix={hasExtension || isBase || variation.isReverseItinerary ? 'Destinations: ' : 'Additional destinations: '}
              locationsVisited={variation.isReverseItinerary ? itineraryDestinations : itineraryDestinationsDiff}
            />}
            {!variation.isReverseItinerary && <>
              <TourV2ItemInclusions inclusions={inclusionsToBeDisplayed} />
              {!showInclusionInline && inclusionsLeft.length > 0 && <TextLink weight="regular" size="small" onClick={showMoreInclusion}>
                +{inclusionsLeft.length} more inclusions
              </TextLink>}
              {showInclusionInline && <TourV2ItemInclusions inclusions={inclusionsLeft} />}
            </>}
          </ListInclusion>
        </VerticalSpacer>
        <LabelsContainer>
          <AgentHubAdditionalCommissionLabel offerId={offer.id} />
          {(availablePackageUpgrades?.length ?? 0) > 1 && <PromotionLabel variant="default">Multiple packages available</PromotionLabel>}
          {variation.canRequestPrivateDepartures && <PromotionLabel variant="default" icon={<LineUsersAltIcon />}>Private departures available</PromotionLabel>}
        </LabelsContainer>
      </VerticalSpacer>
    </DetailsContainer>

    <PricingContainer direction="vertical">
      {!!purchasableOptionDetails && <VerticalSpacer gap={4}>
        <div>
          <PriceRowPriceCaption>From</PriceRowPriceCaption>
          {!pricePerDayDetails?.pricePerDay && <LuxPlusPriceStack size="L" price={Number(purchasableOptionDetails.pricePerPerson)} saleUnit="person" memberPrice={Number(purchasableOptionDetails.memberPrice)} isTour />}
          {!!pricePerDayDetails?.pricePerDay && <LuxPlusPriceStack size="L" price={pricePerDayDetails.pricePerDay.pricePerPersonPerDay} saleUnit="day per person" memberPrice={pricePerDayDetails.pricePerDay.memberPricePerPersonPerDay} isTour />}
          <PriceRowTaxesAndFees />
          {purchasableOptionDetails.hasDiscount && <PriceRowSale value={purchasableOptionDetails.fullPricePerPerson} />}
          <PriceRowCaption>{purchasableOptionDetails.roomTypeName}</PriceRowCaption>
          {
            !!purchasableOptionDetails.valuedAt && <>
              {!pricePerDayDetails?.valueAtPerDay && <PriceRowValueDiscountWithCaption
                size="M"
                originalValue={purchasableOptionDetails.valuedAt.price}
                discountPercentage={isLuxPlusMember ? purchasableOptionDetails.valuedAt.memberSavingsPercentage : purchasableOptionDetails.valuedAt.savingPercentage}
              />}
              {!!pricePerDayDetails?.valueAtPerDay && <PriceRowValueDiscountWithCaption
                size="M"
                originalValue={pricePerDayDetails?.valueAtPerDay}
                discountPercentage={isLuxPlusMember ? purchasableOptionDetails.valuedAt.memberSavingsPercentage : purchasableOptionDetails.valuedAt.savingPercentage}
              />}
            </>
          }
          <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationOptions} calculationType="estimate" />
        </div>
        <PriceRowAgentHubCommission
          offerLocations={tourV2StartLocation}
          offerId={offer.id}
          productType={offer.productType}
        />
      </VerticalSpacer>}
      {!purchasableOptionDetails && <Heading variant="heading5">Sold Out</Heading>}
    </PricingContainer>

    <ButtonsContainer>
      {isSelected && <SelectedButton fit="full-width" kind="primary" variant="dark" nonInteractable tabIndex={-1}>
        Selected
      </SelectedButton>}
      {!isSelected && <TextButton fit="full-width" kind="secondary" onClick={handleTourOptionSelect}>
        View tour
      </TextButton>}
    </ButtonsContainer>
  </Root>
}

export default TourV2VariationCard
